import { useContext, useEffect, useState } from "react"
import classNames from "classnames"
import { FiAlertTriangle, FiArrowRight, FiCheck, FiCheckCircle, FiEye, FiPaperclip, FiPlay, FiSave } from "react-icons/fi"
import { FaQuestionCircle } from "react-icons/fa"
import { Form, Formik } from "formik"
import { twMerge } from "tailwind-merge"
import { toast } from "react-toastify"
import { useHistory, useLocation } from "react-router-dom"

import { Container } from './styles'
import Modal from "../../../../../components/Modal"
import { api, authHeaders } from "../../../../../services/api"
import Table from "../../../../../components/Table"
import { Button, File, Textbox, Textarea, Select, Switch3 as SwitchMultiple } from "../../../../../components/Form"
import { openTabSafari } from "../../../../../util/file"
import formUtils from "../../../../../util/form"
import scrollUtils from "../../../../../util/scroll"
import Spinner from "../../../../../components/Spinner"
import { concatFormacoes } from "../../../EspacoMedico/DadosCadastrais"
import { lancarConfete } from "../../../../../util/confetti"
import { GlobalContext } from "../../../../../contexts/GlobalContext"

export function TimelineTRL({ projeto: projetoBase, permissoes }) {
	const { checkException } = useContext(GlobalContext)

	const history = useHistory()

	const { state } = useLocation()

	const [projeto, setProjeto] = useState(projetoBase)
	const [steps, setSteps] = useState(null)
	const [stepDetalhes, setStepDetalhes] = useState(null)
	const [initialValues, setInitialValues] = useState({})
	const [showConfirmarAvancoEtapa, setShowConfirmarAvancoEtapa] = useState(false)

	async function reloadProjeto() {
		try {
			setProjeto(null)

			const response = await api.get(`projeto/${projeto.id}`, {
				params: {
					scope: 'projeto'
				},
				...authHeaders()
			})

			response.pessoas = response.projeto_pessoas.map(proj_pessoa => {
				const formacoes = proj_pessoa.pessoa.formacoes.reduce((result, formacao) => {
					return [
						...result,
						`${formacao.grau.descricao} em ${concatFormacoes(formacao.formacao)}`
					]
				}, [])

				return {
					...proj_pessoa.pessoa,
					formacao: formacoes.join('<br/>')
				}
			})

			response.documentos = response.documentos.map(doc => ({
				id: doc.id,
				tipo: doc.tipo_documento.descricao,
				descricao: doc.descricao,
				link: doc.arquivo?.link
			}))

			setProjeto(response)
		} catch (e) {
			checkException(e)
		}
	}

	async function loadTRLs(options) {
		try {
			const closeTRLDetalhes = options?.closeTRLDetalhes

			const trlResponse = await api.get('trl', authHeaders())

			const projetoResponse = await api.get(`projeto/${projeto.id}`, {
				params: {
					scope: 'projeto'
				},
				...authHeaders()
			})

			const stepsData = trlResponse.map(trlStep => {
				const dadosProjetoTRL = projetoResponse.trls.find(item => item.trl.nivel === trlStep.nivel)

				return {
					id: trlStep.id,
					projeto_trl_id: dadosProjetoTRL.id,
					projeto_id: projetoResponse.id,
					descricao: trlStep.descricao,
					ajuda: trlStep.link_ajuda,
					cor: trlStep.cor,
					nivel: trlStep.nivel,
					perguntas: dadosProjetoTRL.perguntas.map(item => ({
						id: item.id,
						projeto_trl_pergunta_id: item.id,
						pergunta: item.pergunta,
						resposta: item.resposta,
						resposta_primaria: item.resposta_primaria,
						obrigatorio: item.obrigatorio
					})),
					active: dadosProjetoTRL.status.id === 2,
					executado: dadosProjetoTRL.status.id === 3,
					avaliado: dadosProjetoTRL.status.id === 4,
					avaliacao: {
						observacao: dadosProjetoTRL.avaliacao?.observacao,
						avaliador: dadosProjetoTRL.avaliacao?.avaliador.nome
					},
					documentos: dadosProjetoTRL.documentos.map(doc => ({
						id: doc.id,
						link: doc.arquivo.link,
						tipo: doc.tipo_documento.descricao,
						descricao: doc.descricao,
						projeto_trl_pergunta_id: doc.projeto_trl_pergunta_id
					}))
				}
			})

			setSteps(stepsData)

			if (closeTRLDetalhes) {
				setStepDetalhes(null)
			} else if (stepDetalhes) {
				const stepDetalhe = stepsData.find(s => s.id === stepDetalhes.id)

				setStepDetalhes(stepDetalhe)
			}
		} catch (e) {
			checkException(e)
		}
	}

	function handleAbrirDocumento(doc) {
		openTabSafari(doc.link)
	}

	async function handleRemoverDocumento(id) {
		try {
			await api.delete(`documento/${id}`, authHeaders())

			toast.success('Documento removido do projeto.')

			return true
		} catch (e) {
			checkException(e)
		}

		return false
	}

	useEffect(() => {
		if (steps && state?.etapa_trl_aberta_id) {
			setStepDetalhes(steps.find(s => s.id === +state.etapa_trl_aberta_id))

			if (state.section) {
				setTimeout(() => {
					scrollUtils.toElement(`.${state.section}`)
				}, 800)
			}
		}
	}, [steps, state])

	useEffect(() => {
		loadTRLs()
	}, [])

	useEffect(() => {
		if (stepDetalhes) {
			const obj = stepDetalhes?.perguntas.reduce((result, { projeto_trl_pergunta_id, resposta, resposta_primaria }) => {
				return {
					...result,
					[`pergunta_${projeto_trl_pergunta_id}`]: resposta,
					[`pergunta_primaria_${projeto_trl_pergunta_id}`]: resposta_primaria
				}
			}, {})

			setInitialValues(obj)
		} else {
			setInitialValues({})
		}
	}, [stepDetalhes])

	async function handleIniciarTRL() {
		try {
			await api.patch(`projeto_trl/${projeto.id}/iniciar`, {}, authHeaders())

			reloadProjeto()

			loadTRLs()
		} catch (e) {
			checkException(e)
		}
	}

	async function handleAddDocumento(values) {
		values = formUtils.extractFormValues(values)

		const { arquivo, descricao, pergunta } = values

		const { projeto_trl_id } = stepDetalhes

		try {
			await api.post(`projeto/${projeto.id}/add_document`, {
				arquivo,
				descricao,
				projeto_trl_pergunta_id: pergunta,
				projeto_trl_id
			}, authHeaders())

			toast.success('Documento anexado.')

			loadTRLs()

			history.push({
				pathname: `/projeto/${projeto.id}`,
				state: {
					aba_selecionada_index: 1,
					etapa_trl_aberta_id: stepDetalhes.id,
					section: 'documentos-section'
				}
			})
		} catch (e) {
			checkException(e)
		}
	}

	async function handleSubmitRespostas(values) {
		try {
			const ids = Object.entries(values).reduce((result, [key]) => {
				let [pergunta_id] = key.match(/\d+/g)
				return [...result, +pergunta_id]
			}, [])

			const body = ids.map(id => ({
				projeto_trl_pergunta_id: id,
				pergunta: values[`pergunta_${id}`],
				pergunta_primaria: values[`pergunta_primaria_${id}`]
			}))

			await api.post(`projeto_trl/${stepDetalhes.projeto_trl_id}/respostas`, {
				respostas: body
			}, authHeaders())

			toast.success('As respostas foram salvas.')

			loadTRLs()
		} catch (e) {
			checkException(e)
		}
	}

	async function handleAvancarEtapa() {
		try {
			const { etapa_destino } = await api.patch(`projeto/${stepDetalhes.projeto_id}/avancar_etapa`, {}, authHeaders())

			if (etapa_destino) {
				toast.success('Parabéns! Você avançou para o próximo nível do projeto!')
			} else {
				toast.success('Parabéns! Você concluiu todos os níveis TRL do projeto!')

				lancarConfete()
			}

			setShowConfirmarAvancoEtapa(false)

			loadTRLs({
				closeTRLDetalhes: true
			})

			reloadProjeto()
		} catch (e) {
			checkException(e)
		}
	}

	async function handleAvaliarEtapaTRL(values) {
		try {
			const { observacao } = values

			await api.post(`projeto/${projeto.id}/trl/${stepDetalhes.projeto_trl_id}/avaliar`, { observacao }, authHeaders())

			loadTRLs()

			reloadProjeto()

			const eUltimaEtapa = stepDetalhes.nivel === projeto.trls.at(-1).trl.nivel

			if (eUltimaEtapa) {
				toast.success('Parabéns! Todos os níveis foram avaliados!')

				lancarConfete()
			} else {
				toast.success('Avaliação concluída com sucesso!')
			}
		} catch (e) {
			checkException(e)
		}
	}

	if (!steps || !projeto) {
		return <Spinner />
	}

	const { eExecutor, eAvaliador } = permissoes
	const etapaAtual = projeto.trls.find(trl => trl.status.id === 2)
	const perguntasObrigatoriasRepondidas = stepDetalhes?.perguntas
		.filter(({ obrigatorio }) => !!obrigatorio)
		.every(({ resposta_primaria, resposta }) => resposta_primaria && resposta)
	const respostasPositivasSemJustificativas = (!!stepDetalhes && stepDetalhes.perguntas.some(perg => perg.resposta_primaria && !perg.resposta))
	const etapaAtualAberta = !!stepDetalhes && stepDetalhes?.nivel === etapaAtual?.trl.nivel
	const etapaAtualFoiExecutada = !!stepDetalhes && (stepDetalhes.executado || stepDetalhes.avaliado)
	const eEtapaFutura = !!stepDetalhes && !etapaAtualFoiExecutada && !stepDetalhes.active
	const eUltimaEtapa = etapaAtual?.trl.nivel === projeto.trls.at(-1).trl.nivel
	const deveMostrarStart = projeto.trls.every(trl => trl.status.id === 1) && eExecutor
	const trlExecutadoCompleto = projeto.trls.every(trl => trl.status.id >= 3) // todos executados
	const trlConcluido = projeto.trls.every(trl => trl.status.id === 4) // todos avaliados
	const quantidadeEtapasAAvaliar = trlExecutadoCompleto && projeto.trls.filter(trl => trl.status.id === 3).length

	function getTRLBadge(stepData) {
		if (stepData?.avaliado) {
			return (
				<div className="bg-emerald-600 p-3 rounded-md h-fit font-semibold">
					Este nível já foi avaliado!
				</div>
			)
		}

		if (stepData?.executado) {
			return (
				<div className="px-2 py-1 rounded-md bg-emerald-600 w-fit flex gap-1 items-center font-bold">
					<FiCheck size={16} />
					Nível executado
				</div>
			)
		}

		if (stepDetalhes?.active) {
			return (
				<div className="px-2 py-1 rounded-md bg-sky-600 w-fit flex gap-2 items-center font-semibold">
					Este nível está em execução
				</div>
			)
		}

		return (
			<div></div>
		)
	}

	return (
		<>
			<Container className="flex flex-col mt-10">
				{deveMostrarStart ? (
					<div className="self-center flex flex-col items-center">
						<Button className="white peer" onClick={handleIniciarTRL}>
							Iniciar agora
							<FiPlay size={16} />
						</Button>
					</div>
				) : (
					<div className="flex flex-col gap-1">
						{trlConcluido ? (
							<div className="bg-emerald-700 p-3 rounded-md w-fit self-center font-semibold">
								O TRL está concluído!
							</div>
						) : trlExecutadoCompleto ? (
							<>
								<div className="bg-emerald-700 p-3 rounded-md w-fit self-center font-semibold">
									O TRL está executado!
								</div>

								{quantidadeEtapasAAvaliar >= 1 && (
									<div className="text-xs bg-yellow-500 p-2 rounded-md w-fit flex gap-1 items-center self-center text-slate-800 font-normal">
										<FiAlertTriangle size={14} color="#242424" />

										{quantidadeEtapasAAvaliar > 1 ?
											`Existem ${quantidadeEtapasAAvaliar} avaliações pendentes` :
											`Existe ${quantidadeEtapasAAvaliar} avaliação pendente`
										}
									</div>
								)}
							</>
						) : null}

						<div className="relative mt-10">
							<ul className="timeline flex items-center justify-between gap-4">
								{steps.map((step, idx) => {
									const eExtremidade = steps.length - 1 === idx
									const eIrrelevante = !step.executado && !step.avaliado && !step.active

									return (
										<li
											key={step.nivel}
											className={twMerge(
												'bg-white border-4 text-slate-800 text-lg rounded-full p-2 w-24 h-24 flex justify-center items-center flex gap-2 items-baseline text-center shadow-lg transition-all cursor-pointer relative',
												`animated fadeIn faster delay-${idx * 100}ms`,
												`border-[${step.cor}]`,
												classNames(
													{
														'hover:scale-105': !step.active,
														[`border-[${step.cor}88]`]: eIrrelevante,
														'scale-75 hover:scale-90': eExtremidade,
														'scale-125 hover:scale-[1.3]': step.active,
														'scale-[1.1] hover:scale-[1.2]': step.active && eExtremidade
													}
												)
											)}
											style={{
												zIndex: 2
											}}
											onClick={() => {
												setStepDetalhes(step)
											}}
										>
											<div className={classNames(
												'flex flex-col mt-3',
												{
													'opacity-50 hover:opacity-90': eIrrelevante
												}
											)}>
												<span style={{ fontSize: eExtremidade ? 14 : 11 }} className="leading-normal">Nível</span>

												<span className="text-xl font-semibold">
													{step.nivel}
												</span>
											</div>

											{step.active ? (
												<div
													style={{
														fontSize: 10,
														borderColor: step.cor,
														borderWidth: 2,
														backgroundColor: '#fff',
														padding: '0 2px',
														borderRadius: 6,
														position: 'absolute',
														bottom: -22,
														lineHeight: 2,
														display: 'flex',
														gap: 1,
														alignItems: 'center'
													}}
												>
													<FiPlay className="text-sky-500" size={12} />
													<span className="text-sky-500 font-semibold">Em execução</span>
												</div>
											) : step.avaliado ? (
												<div
													style={{
														fontSize: 12,
														padding: '0 2px',
														borderRadius: 6,
														position: 'absolute',
														bottom: -22,
														lineHeight: 2,
														display: 'flex',
														gap: 2,
														alignItems: 'center',
														fontWeight: 500
													}}
													className="bg-lime-600 border-2 border-lime-600"
												>
													<FiCheckCircle className="text-emerald-100" size={18} />
													<span className="text-white">Avaliado</span>
												</div>
											) : step.executado ? (
												<>
													<div
														style={{
															fontSize: 12,
															borderColor: step.cor,
															borderWidth: 2,
															backgroundColor: '#fff',
															padding: '0 2px',
															borderRadius: 6,
															position: 'absolute',
															bottom: -22,
															lineHeight: 2,
															display: 'flex',
															gap: 2,
															alignItems: 'center',
															fontWeight: 500
														}}
													>
														<FiCheck className="text-emerald-500" size={18} />
														<span className="text-emerald-600">Executado</span>
													</div>

													<div className="bg-yellow-500 text-slate-800 p-2 rounded-full text-xs absolute -top-5 leading-2 flex font-normal items-center group">
														<FiAlertTriangle className="text-slate-800" size={16} />

														<span
															className={
																classNames(
																	'text-slate-800 w-0 h-0 group-hover:ml-1 group-hover:h-fit overflow-hidden transition-all',
																	{
																		'group-hover:w-[2.5rem]': eAvaliador,
																		'group-hover:w-[7rem]': !eAvaliador
																	}
																)}
														>
															{eAvaliador ? 'Avaliar' : 'Avaliação pendente'}
														</span>
													</div>
												</>
											) : null}
										</li>
									)
								})}
							</ul>
						</div>
					</div>
				)}
			</Container>

			<Modal
				isOpen={!!stepDetalhes}
				handleClose={() => {
					setStepDetalhes(null)
				}}
				title={`Nível ${stepDetalhes?.nivel || ''} - ${stepDetalhes?.descricao || ''}`}
			>
				{stepDetalhes ? (
					<div className="modal-etapa-trl flex flex-col">
						<div className="flex justify-between gap-8 mx-2">
							<div className="flex flex-col gap-2 mt-3">
								{getTRLBadge(stepDetalhes)}

								{etapaAtualFoiExecutada && !stepDetalhes.avaliado && (
									<div className="bg-yellow-500 p-2 text-sm rounded-md text-slate-800 w-fit flex items-center gap-2">
										<FiAlertTriangle size={17} color="#242424" />
										A avaliação deste nível está pendente
									</div>
								)}
							</div>

							{stepDetalhes.ajuda ? (
								<Button
									tooltip={stepDetalhes.ajuda}
									tooltipPosition="left"
									themeMode="light"
									className="transparent w-fit"
								>
									Ajuda
									<FaQuestionCircle size={15} />
								</Button>
							) : <div></div>}
						</div>

						{stepDetalhes.avaliado && (
							<div className="flex flex-col gap-2 mx-2 mt-4">
								<h2 className="font-bold text-lg">Avaliação</h2>
								<span className="font-semibold">{`Avaliador: ${stepDetalhes?.avaliacao.avaliador}`}</span>
								<p>{stepDetalhes?.avaliacao.observacao}</p>
							</div>
						)}

						<div className="m-2 flex flex-col border-t border-gray-400 pt-4 mt-4">
							<h2 className="font-semibold mb-4" style={{ fontSize: 18 }}>Módulo</h2>

							{stepDetalhes.nivel === etapaAtual?.trl.nivel && eExecutor ? (
								<Formik
									onSubmit={handleSubmitRespostas}
									initialValues={initialValues}
									enableReinitialize
								>
									{({ values }) => (
										<Form className="flex flex-col">
											<ul className="flex flex-col gap-3">
												{stepDetalhes.perguntas.map(({ projeto_trl_pergunta_id, pergunta, obrigatorio }, idx) => (
													<li key={projeto_trl_pergunta_id}>
														<p className="font-semibold mb-2">{`${idx + 1} - ${pergunta} ${obrigatorio ? '*' : ''}`}</p>

														<SwitchMultiple
															label=""
															name={`pergunta_primaria_${projeto_trl_pergunta_id}`}
															className="mt-3"
															items={[
																'Não se aplica',
																'Em curso',
																'Pronto'
															]}
														/>

														<Textarea
															name={`pergunta_${projeto_trl_pergunta_id}`}
															label="Justifique abaixo:"
															containerStyle={{
																display: [1, 2].includes(values[`pergunta_primaria_${projeto_trl_pergunta_id}`]) ? 'block' : 'none'
															}}
														/>
													</li>
												))}
											</ul>

											<p style={{ fontSize: 12 }} className="mt-4">* Módulo obrigatório.</p>

											<Button type="submit" className="white self-end">
												Salvar respostas
												<FiSave size={16} />
											</Button>
										</Form>
									)}
								</Formik>
							) : (
								<ul>
									{stepDetalhes.perguntas.map(({ pergunta, resposta, resposta_primaria }, idx) => (
										<li key={pergunta} className="mb-4 space-y-1">
											<p>{`${idx + 1} - ${pergunta}`}</p>

											{etapaAtualFoiExecutada && (
												<>
													{resposta ? (
														<div
															className="bg-white py-2 px-3 rounded-md text-slate-800"
															dangerouslySetInnerHTML={{
																__html: resposta.replace(/\n/, '<br/>')
															}}
														/>
													) : !resposta_primaria ? (
														<p className="ml-4 italic">Não se aplica.</p>
													) : null}
												</>
											)}
										</li>
									))}
								</ul>
							)}

							{!eEtapaFutura ? (
								<>
									<h2 className="documentos-section font-semibold mb-1 border-t border-gray-400 pt-4 mt-2" style={{ fontSize: 18 }}>Documentos</h2>

									<div className="flex flex-col md:grid md:grid-cols-[2fr_1.5fr] gap-8 mb-6">
										{etapaAtualAberta && eExecutor && (
											<div>
												<Formik
													onSubmit={handleAddDocumento}
													initialValues={{
														arquivo: null,
														descricao: null,
														pergunta: null
													}}
												>
													{({ setFieldValue, errors, values }) => {
														const podeSubmeter = !!values.arquivo && !!values.descricao

														return (
															<Form className="flex flex-col lg:grid lg:grid-cols-[1fr_1fr] lg:gap-x-4 lg:col-span-2">
																<File
																	name="arquivo"
																	label="Anexe o documento"
																	onSuccess={fileId => setFieldValue('arquivo', fileId)}
																	format="square"
																	error={errors.arquivo}
																	style={{ width: 'calc(100% - 16px)' }}
																	previewSize={['100%', '180px']}
																/>

																<div className="flex flex-col">
																	<Textbox
																		name="descricao"
																		label="Nome do documento"
																	/>

																	<Select
																		name="pergunta"
																		label="Pré-requisito relacionado"
																		onChange={(selected, meta) => {
																			setFieldValue(meta.name, selected)
																		}}
																		options={stepDetalhes.perguntas.map((pergunta, idx) => ({
																			label: `Pré-requisito ${idx + 1}`,
																			value: pergunta.projeto_trl_pergunta_id
																		}))}
																	/>

																	<Button type="submit" disabled={!podeSubmeter} className="transparent w-fit self-end">
																		Anexar documento
																		<FiPaperclip size={16} />
																	</Button>
																</div>
															</Form>
														)
													}}
												</Formik>
											</div>
										)}

										<Table
											headers={[
												{ name: 'descricao', value: 'Documento' },
												{ name: 'pergunta_referencia', value: 'Pré-requisito' }
											]}
											data={stepDetalhes.documentos.map(doc => {
												const perguntaReferenciaNumero = stepDetalhes.perguntas.findIndex(p => p.id === doc.projeto_trl_pergunta_id) + 1

												return {
													...doc,
													pergunta_referencia: perguntaReferenciaNumero
												}
											})}
											actions={[
												{
													action: handleAbrirDocumento,
													icon: FiEye,
													iconSize: 16,
													name: 'Abrir'
												}
											]}
											handleDelete={etapaAtualAberta ? handleRemoverDocumento : undefined}
											confirmExclusion={{
												template: 'Deseja realmente remover o documento <b>#attr#</b> do projeto?',
												attr: 'tipo'
											}}
											emptyLabel="Nenhum documento anexado neste nível"
										/>
									</div>

									{eExecutor && !perguntasObrigatoriasRepondidas && (
										<div className="mx-2 p-2 rounded-md bg-yellow-500 text-slate-800 h-fit w-fit self-end">
											Para avançar para o próximo nível, responda a todos os módulos obrigatórios.
										</div>
									)}

									{eExecutor && respostasPositivasSemJustificativas && (
										<div className="mx-2 p-2 rounded-md bg-yellow-500 text-slate-800 h-fit w-fit self-end">
											Para avançar para o próximo nível, justifique todos os módulos nos quais você respondeu positivamente.
										</div>
									)}

									{!etapaAtualFoiExecutada && eExecutor && (
										<div className="mt-2 flex justify-end">
											<Button
												onClick={() => { setShowConfirmarAvancoEtapa(true) }}
												className="white"
												disabled={respostasPositivasSemJustificativas || !perguntasObrigatoriasRepondidas}
											>
												Avançar nível
												<FiArrowRight size={16} />
											</Button>
										</div>
									)}

									{etapaAtualAberta && eAvaliador && (
										<div className="mx-2 p-2 rounded-md bg-yellow-500 text-slate-800 h-fit w-fit self-center flex items-center gap-2">
											<FiAlertTriangle size={17} color="#242424" />
											Este nível ainda não foi executado. Você poderá avaliá-lo quando for finalizado.
										</div>
									)}
								</>
							) : (
								<div className="mx-2 p-2 rounded-md bg-yellow-500 text-slate-800 h-fit w-fit self-center flex items-center gap-2">
									<FiAlertTriangle size={17} color="#242424" />

									{eExecutor ?
										'Você ainda não atingiu esta etapa.' :
										'O TRL do projeto ainda não atingiu esta etapa.'
									}
								</div>
							)}

							{eAvaliador && !stepDetalhes.avaliado && (
								<>
									{etapaAtualFoiExecutada && (
										<details className="my-4 peer open:border open:border-white rounded-md group">
											<summary className="border border-white rounded-md py-2 px-3 cursor-pointer hover:bg-[#fff1] list-none group-open:border-0 font-semibold text-center">
												Avaliar este nível TRL
											</summary>

											<div className="px-4 py-2">
												<Formik
													onSubmit={handleAvaliarEtapaTRL}
													initialValues={{ observacao: '' }}
												>
													{() => (
														<Form className="flex flex-col">
															<Textarea
																name="observacao"
																label="Observação"
															/>

															<Button type="submit" className="white self-end">
																Confirmar avaliação
																<FiCheck size={16} />
															</Button>
														</Form>
													)}
												</Formik>
											</div>
										</details>
									)}
								</>
							)}
						</div>
					</div>
				) : null}
			</Modal>

			<Modal
				isOpen={showConfirmarAvancoEtapa}
				title="Confirmação"
				handleClose={() => {
					setShowConfirmarAvancoEtapa(false)
				}}
			>
				<div className="flex flex-col mx-2">
					{eUltimaEtapa ? (
						<p>Deseja realmente finalizar o último nível TRL?</p>
					) : (
						<p>Deseja realmente avançar para o próximo nível TRL?</p>
					)}

					<Button onClick={handleAvancarEtapa} className="white w-fit self-end">
						Confirmar
					</Button>
				</div>
			</Modal>
		</>
	)
}
